import dynamic from 'next/dynamic';
import useViewport from '#hooks/useViewport';
import PageSection from '#components/sections/page-section/PageSection';
import dayjs from 'dayjs';
import {
  LivestreamBannerDocumentBlock,
  LivestreamState,
} from '#types/generated/graphql-types';
import getRoute from '#utils/getRoute';
import useTranslation from 'next-translate/useTranslation';

const LivestreamBannerSmall = dynamic(
  () => import('#components/sections/livestream-banner/LivestreamBannerSmall'),
  { ssr: false }
);
const LivestreamBannerMedium = dynamic(
  () => import('#components/sections/livestream-banner/LivestreamBannerMedium'),
  { ssr: false }
);
const LivestreamBannerLarge = dynamic(
  () => import('#components/sections/livestream-banner/LivestreamBannerLarge'),
  { ssr: false }
);

type Props = {
  block: LivestreamBannerDocumentBlock;
};

const LivestreamBanner = ({ block }: Props) => {
  const { isSmall, isMedium } = useViewport();
  const { t } = useTranslation();

  const { media, livestreamIri, description, title } = block;

  if (!block?.media?.details) {
    return null;
  }

  const routeParams = {
    iri: livestreamIri,
    slug: media.slug,
    seriesSlug: media.seriesSlug,
  };
  const route = getRoute(routeParams, t);

  let Component = LivestreamBannerLarge;

  if (isSmall) {
    Component = LivestreamBannerSmall;
  }
  if (isMedium) {
    Component = LivestreamBannerMedium;
  }

  const start = dayjs(media?.details?.startDate);
  const end = dayjs(media?.details?.endDate);
  const duration =
    media?.details?.streamState !== LivestreamState.Archived
      ? end.diff(start)
      : media?.mediaDuration;

  return (
    <PageSection>
      <Component
        title={title}
        description={description}
        startDate={media?.details?.startDate}
        endDate={media?.details?.endDate}
        duration={duration}
        streamState={media?.details?.streamState}
        image={media?.teaserImageFormats?.sixteenByNine?.medium}
        href={route}
      />
    </PageSection>
  );
};

export default LivestreamBanner;
